import { computed, onMounted, ref, type Ref } from "vue";
import configurationAPI from "@/apiServices/configurationAPI";

/**
 * The environment label shown across the top of the app — "Training", "QA", "Pre-Prod" (PR-869).
 *
 * The text lives in the `Config.Config` table under `global-warning-message`, the same key EdgeOS/Rio
 * uses for its own banner. Standing up a new environment is therefore a row, not a release: the ask
 * behind this ticket was "where the title is going to be configured", with QA and pre-prod next.
 *
 * `isDevMode` stays a fallback rather than a driver, so dev keeps its existing "Development" treatment
 * with no row inserted and this change cannot regress it. Production has neither, and shows nothing.
 *
 * Pass the `isDevMode` ref from the caller's own `useBuildFooterData()` — taking it as an argument
 * keeps this to one `/api/build` request per page instead of two.
 *
 * @param devModeLabel What to show in dev when no row is configured. Per-surface, because the two
 *   existing badges are not worded the same — the header reads "Development", the login card "DEV" —
 *   and a configured environment should not be the thing that quietly restyles dev.
 */
export function useEnvironmentLabel(isDevMode: Ref<boolean>, devModeLabel: string) {
  const configuredLabel = ref("");

  async function fetchEnvironmentLabel() {
    try {
      const response = await configurationAPI.getGlobalWarningMessage();
      configuredLabel.value = (response.data ?? "").trim();
    } catch (error) {
      // Deliberately no toast: a banner that cannot be read is not something the user can act on, and
      // nagging every page load would be worse than the missing label. Same call as useBuildFooterData.
      console.error("Error fetching environment label:", error);
    }
  }

  /** The text to render, or '' when this environment has no label (production). */
  const environmentLabel = computed(
    () => configuredLabel.value || (isDevMode.value ? devModeLabel : ""),
  );

  /** Whether to apply the red non-production treatment that goes with the label. */
  const showEnvironmentTheme = computed(() => environmentLabel.value.length > 0);

  onMounted(() => {
    void fetchEnvironmentLabel();
  });

  return {
    environmentLabel,
    showEnvironmentTheme,
    fetchEnvironmentLabel,
  };
}
